import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";
import FUNCTIONS_URLS from '../../../../Config/functions';
import { adminAuthHeaders } from '../../../../Config/adminAuthHeaders';


const FixBalanceFromLedgersTool = () => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [progress, setProgress] = useState(0);

  const CLOUD_FUNCTION_URL = FUNCTIONS_URLS.FixAllUserBalancesFromLedgers;

  const analyzeBalances = async () => {
    setAnalyzing(true);
    setProgress(0);
    setAnalysisResult(null);

    try {
      setProgress(20);
      const response = await axios.post(
        CLOUD_FUNCTION_URL,
        { action: "analyze" },
        {
          headers: await adminAuthHeaders(),
          timeout: 300000, // 5 minutes
        }
      );

      setProgress(100);
      setAnalysisResult(response.data);
      setAnalyzing(false);

      if (response.data.success && response.data.summary.usersNeedingCorrection > 0) {
        Swal.fire({
          icon: "info",
          title: "Análise Completa",
          html: `
            <div style="text-align: left;">
              <p><strong>Usuários precisando correção:</strong> ${response.data.summary.usersNeedingCorrection}</p>
              <p><strong>Diferença total:</strong> $${response.data.totals.totalDifference.toFixed(2)}</p>
              <p><strong>Diferença líquida:</strong> $${response.data.totals.netDifference.toFixed(2)}</p>
              <p style="margin-top: 10px;">Revise as correções abaixo antes de executar.</p>
            </div>
          `,
          timer: 5000,
        });
      } else if (response.data.success && response.data.summary.usersNeedingCorrection === 0) {
        Swal.fire({
          icon: "success",
          title: "Nenhuma Correção Necessária",
          text: "Todos os balances estão corretos!",
          timer: 3000,
        });
      }
    } catch (error) {
      console.error("Error analyzing balances:", error);
      setAnalyzing(false);
      setProgress(0);
      
      Swal.fire({
        icon: "error",
        title: "Erro na Análise",
        text: error.response?.data?.error || error.message || "Erro desconhecido ao analisar balances",
      });
    }
  };

  const executeCorrections = async () => {
    if (!analysisResult || !analysisResult.corrections || analysisResult.corrections.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Nenhuma Correção",
        text: "Por favor, execute a análise primeiro.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "⚠️ Confirmar Execução?",
      html: `
        <div style="text-align: left;">
          <p><strong>Você está prestes a corrigir ${analysisResult.summary.usersNeedingCorrection} usuários!</strong></p>
          <p>Isso irá:</p>
          <ul style="text-align: left; margin: 10px 0;">
            <li>Atualizar <strong>net_balance</strong> para o valor do ledger mais recente</li>
            <li>Atualizar <strong>_last_balance_sync</strong> (trigger para app atualizar)</li>
            <li>Adicionar campos de auditoria (<strong>balance_corrected_at</strong>, <strong>balance_correction_note</strong>)</li>
          </ul>
          <p style="color: #ff9800; font-weight: bold; margin-top: 15px;">
            Diferença total a corrigir: $${Math.abs(analysisResult.totals.netDifference).toFixed(2)}
          </p>
          <p style="color: red; font-weight: bold; margin-top: 10px;">
            Esta ação não pode ser desfeita facilmente!
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f52a2d",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sim, executar correções",
      cancelButtonText: "Cancelar",
      input: "text",
      inputPlaceholder: "Digite 'EXECUTAR' para confirmar",
      inputValidator: (value) => {
        if (value !== "EXECUTAR") {
          return "Você deve digitar 'EXECUTAR' para confirmar";
        }
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    setExecuting(true);
    setProgress(0);

    try {
      setProgress(20);
      const response = await axios.post(
        CLOUD_FUNCTION_URL,
        { action: "execute" },
        {
          headers: await adminAuthHeaders(),
          timeout: 600000, // 10 minutes
        }
      );

      setProgress(100);
      setExecuting(false);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "✅ Correções Executadas!",
          html: `
            <div style="text-align: left;">
              <p><strong>Usuários atualizados:</strong> ${response.data.updated}</p>
              ${response.data.errors > 0 ? `<p style="color: red;"><strong>Erros:</strong> ${response.data.errors}</p>` : ""}
            </div>
          `,
          timer: 5000,
        });

        // Refresh analysis
        setTimeout(() => {
          analyzeBalances();
        }, 2000);
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro na Execução",
          text: response.data.error || "Erro desconhecido ao executar correções",
        });
      }
    } catch (error) {
      console.error("Error executing corrections:", error);
      setExecuting(false);
      setProgress(0);
      
      Swal.fire({
        icon: "error",
        title: "Erro na Execução",
        text: error.response?.data?.error || error.message || "Erro desconhecido ao executar correções",
      });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: "#fff", fontWeight: "bold" }}>
        🔧 Fix Balance from Ledgers Tool
      </Typography>

      <Alert severity="info" sx={{ mb: 3, backgroundColor: "rgba(33, 150, 243, 0.1)", border: "1px solid #2196f3" }}>
        <Typography variant="body1" sx={{ color: "#fff", mb: 1 }}>
          <strong>O que esta ferramenta faz:</strong>
        </Typography>
        <Box component="ul" sx={{ color: "#fff", fontSize: "0.95rem", pl: 3, mb: 1 }}>
          <li>Busca o <strong>ledger mais recente</strong> de cada usuário (ordenado por data)</li>
          <li>Compara o <strong>current_balance</strong> do ledger com o <strong>net_balance</strong> do usuário</li>
          <li>Identifica discrepâncias (diferença &gt; $0.01)</li>
          <li>Atualiza o <strong>net_balance</strong> para o valor do ledger mais recente</li>
          <li>Atualiza <strong>_last_balance_sync</strong> (trigger para app atualizar)</li>
        </Box>
        <Typography variant="body2" sx={{ color: "#2af598", fontWeight: "bold", mt: 1 }}>
          ✅ Seguro: Mesma lógica do botão "Fix Balance" individual, aplicada a todos os usuários automaticamente.
        </Typography>
      </Alert>

      {/* Controls */}
      <Card sx={{ mb: 3, backgroundColor: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="primary"
                startIcon={analyzing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                onClick={analyzeBalances}
                disabled={loading || analyzing || executing}
                sx={{ mr: 2, mb: { xs: 2, md: 0 } }}
              >
                {analyzing ? "Analisando..." : "Analisar Balances"}
              </Button>
              
              <Button
                variant="contained"
                color="warning"
                startIcon={executing ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
                onClick={executeCorrections}
                disabled={loading || analyzing || executing || !analysisResult || analysisResult.summary.usersNeedingCorrection === 0}
                sx={{ mb: { xs: 2, md: 0 } }}
              >
                {executing ? "Executando..." : "Executar Correções"}
              </Button>
            </Grid>
            
            {(analyzing || executing) && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" sx={{ color: "#fff", minWidth: 50 }}>
                    {progress}%
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Analysis Results */}
      {analysisResult && (
        <>
          {/* Summary */}
          <Card sx={{ mb: 3, backgroundColor: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: "#fff", mb: 2, fontWeight: "bold" }}>
                📊 Resumo da Análise
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2, backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
                      {analysisResult.summary.totalUsers}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                      Total de Usuários
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2, backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ color: "#2af598", fontWeight: "bold" }}>
                      {analysisResult.summary.usersWithLedgers}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                      Com Ledgers
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2, backgroundColor: "rgba(255, 255, 255, 0.05)", borderRadius: 2 }}>
                    <Typography variant="h4" sx={{ color: "#2af598", fontWeight: "bold" }}>
                      {analysisResult.summary.usersWithCorrectBalance}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                      Balance Correto
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: "center", p: 2, backgroundColor: "rgba(245, 42, 45, 0.2)", borderRadius: 2, border: analysisResult.summary.usersNeedingCorrection > 0 ? "2px solid #f52a2d" : "none" }}>
                    <Typography variant="h4" sx={{ color: analysisResult.summary.usersNeedingCorrection > 0 ? "#f52a2d" : "#2af598", fontWeight: "bold" }}>
                      {analysisResult.summary.usersNeedingCorrection}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
                      Precisando Correção
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {analysisResult.summary.usersNeedingCorrection > 0 && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: 2, border: "1px solid #ff9800" }}>
                  <Typography variant="body1" sx={{ color: "#fff", mb: 1, fontWeight: "bold" }}>
                    💰 Totais:
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ color: "#aaa" }}>Diferença Total Absoluta:</Typography>
                      <Typography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
                        {formatCurrency(analysisResult.totals.totalDifference)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ color: "#aaa" }}>Total a Adicionar:</Typography>
                      <Typography variant="h6" sx={{ color: "#2af598", fontWeight: "bold" }}>
                        {formatCurrency(analysisResult.totals.totalPositive)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" sx={{ color: "#aaa" }}>Total a Subtrair:</Typography>
                      <Typography variant="h6" sx={{ color: "#f52a2d", fontWeight: "bold" }}>
                        {formatCurrency(analysisResult.totals.totalNegative)}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="body2" sx={{ color: "#ff9800", mt: 2, fontWeight: "bold" }}>
                    Diferença Líquida: {formatCurrency(Math.abs(analysisResult.totals.netDifference))} 
                    {analysisResult.totals.netDifference > 0 ? " (maioria dos balances está abaixo do ledger)" : " (maioria dos balances está acima do ledger)"}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Corrections List */}
          {analysisResult.summary.usersNeedingCorrection > 0 && (
            <Card sx={{ backgroundColor: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ color: "#fff", fontWeight: "bold" }}>
                    📋 Correções Necessárias ({analysisResult.corrections.length} usuários)
                  </Typography>
                  <Chip
                    label={`Top ${Math.min(20, analysisResult.corrections.length)}`}
                    color="warning"
                    size="small"
                  />
                </Box>
                
                <TableContainer component={Paper} sx={{ backgroundColor: "rgba(0, 0, 0, 0.2)", maxHeight: 600 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", color: "#fff", fontWeight: "bold" }}>
                          #
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", color: "#fff", fontWeight: "bold" }}>
                          Usuário
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", color: "#fff", fontWeight: "bold" }}>
                          Email
                        </TableCell>
                        <TableCell align="right" sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", color: "#fff", fontWeight: "bold" }}>
                          Balance Atual
                        </TableCell>
                        <TableCell align="right" sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", color: "#fff", fontWeight: "bold" }}>
                          Balance do Ledger
                        </TableCell>
                        <TableCell align="right" sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", color: "#fff", fontWeight: "bold" }}>
                          Diferença
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)", color: "#fff", fontWeight: "bold" }}>
                          Ledger Mais Recente
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysisResult.corrections.slice(0, 20).map((corr, index) => (
                        <TableRow
                          key={corr.userId}
                          sx={{
                            "&:nth-of-type(odd)": { backgroundColor: "rgba(255, 255, 255, 0.02)" },
                            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
                          }}
                        >
                          <TableCell sx={{ color: "#fff" }}>{index + 1}</TableCell>
                          <TableCell sx={{ color: "#fff" }}>{corr.userName}</TableCell>
                          <TableCell sx={{ color: "#aaa", fontSize: "0.9rem" }}>{corr.userEmail}</TableCell>
                          <TableCell align="right" sx={{ color: "#aaa" }}>
                            {formatCurrency(corr.currentNetBalance)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#2af598", fontWeight: "bold" }}>
                            {formatCurrency(corr.ledgerBalance)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: corr.difference > 0 ? "#2af598" : "#f52a2d",
                              fontWeight: "bold",
                            }}
                          >
                            {corr.difference > 0 ? "+" : ""}
                            {formatCurrency(corr.difference)}
                          </TableCell>
                          <TableCell sx={{ color: "#aaa", fontSize: "0.85rem" }}>
                            {corr.mostRecentLedgerDate?.toDate
                              ? corr.mostRecentLedgerDate.toDate().toLocaleString("pt-BR")
                              : new Date(corr.mostRecentLedgerDate?.seconds * 1000 || 0).toLocaleString("pt-BR")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {analysisResult.corrections.length > 20 && (
                  <Alert severity="info" sx={{ mt: 2, backgroundColor: "rgba(33, 150, 243, 0.1)" }}>
                    <Typography variant="body2" sx={{ color: "#fff" }}>
                      Mostrando apenas os top 20 (maiores diferenças). Total de {analysisResult.corrections.length} correções necessárias.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Info Alert */}
      {!analysisResult && (
        <Alert severity="info" sx={{ mt: 3, backgroundColor: "rgba(33, 150, 243, 0.1)", border: "1px solid #2196f3" }}>
          <Typography variant="body2" sx={{ color: "#fff" }}>
            <strong>Como usar:</strong>
            <br />
            1. Clique em <strong>"Analisar Balances"</strong> para identificar discrepâncias
            <br />
            2. Revise as correções necessárias na tabela abaixo
            <br />
            3. Clique em <strong>"Executar Correções"</strong> para aplicar as correções automaticamente
            <br />
            <br />
            <strong>⚠️ Importante:</strong> Esta ferramenta usa a mesma lógica do botão "Fix Balance" individual, aplicada a todos os usuários de uma vez. Sempre faça backup antes de executar correções em massa.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default FixBalanceFromLedgersTool;
